/**
 * Revision History
 * 
 * 11/20/2021   ptkim           Create
 */
import _ from 'lodash';
import {compareIso, isIsoDateTime} from '../../dateUtils';
import {DBMappingInfo} from '../../framework.core/data/DBMappingInfo';
import {setCleanStateStorage} from '../../hooks/useDirtyCheck2';
import {IDictionary} from './TrackingModule';

export interface ChangeTrackable {
  key: string | undefined,    // Key property name
  changedProps: ChangedProperty[],
  complexChangedProps: ComplexChangedProperty[],
  changedCollectionProps: CollectionChangedProperty[],
  isDirty: boolean,
  trackable: boolean,
  noTrackProps: string[],
  //<save-sqlinfo: Make these as static function of TrackableEntity
  // acceptChanges: Function,
  // rejectChanges: Function,
  //save-sqlinfo>
  [x:string]:any
};

/**
 * { propertyName: string, oldValue: any, newValue: any }
 */
export interface ChangedProperty {
  propertyName: string,
  oldValue: any,
  newValue: any
};

/**
 * { keyPropertyName: string|undefined, keyValue: any, changeTrackable: ChangeTrackable | null }
 */
export interface CollectionChangedItem {
  keyPropertyName: string|undefined,
  keyValue: any,
  changeTrackable: ChangeTrackable | null
};

/**
 * { complexName: string,  complexItem: CollectionChangedItem | null }
 */
export interface ComplexChangedProperty {
  complexName: string,
  complexItem: CollectionChangedItem | null
};

/**
 * { collectionName: string,  addedItems: CollectionChangedItem[], changedItems: CollectionChangedItem[], deletedItems: CollectionChangedItem[] }
 */
export interface CollectionChangedProperty {
  collectionName: string,
  addedItems: CollectionChangedItem[],
  changedItems: CollectionChangedItem[],
  deletedItems: CollectionChangedItem[]
}

// function setCleanState(cs:any, skipIfExists:boolean = false) {
//   if (skipIfExists && !_.isEqual(getScreenMemory(), {})) {
//     return;
//   }

//   // window.console.log(cs);

//   storeInScreenMemory({
//     __screenCleanState: cs,
//   });
// }

export abstract class TrackableEntity implements ChangeTrackable {
  key: string | undefined = undefined;
  
  changedProps: ChangedProperty[] = [];
  complexChangedProps: ComplexChangedProperty[] = [];
  changedCollectionProps: CollectionChangedProperty[] = [];
  //isDirty: boolean;
  private _trackable: boolean = false;
  get trackable() { return this._trackable;}
  set trackable(value) { 
    this._trackable = value;
    this.setChildTrackable(value);
  };

  setChildTrackable(value:boolean) {
    // Overridable
  }

  noTrackProps: string[] = ['key', 'changedProps', 'complexChangedProps', 'changedCollectionProps', '_trackable', 'noTrackProps', 'getdateProps'];
  //acceptChanges: Function;
  //rejectChanges: Function;
  getdateProps: string[] = [];
  

  static nextId = 0;
  public GetNextId(): number {
    --TrackableEntity.nextId;
    return TrackableEntity.nextId;
  }
  public get isDirty(): boolean {
    return this.changedProps.length > 0 || this.complexChangedProps.length > 0 || this.changedCollectionProps.length > 0;;
  }

  static getAcceptedEntity = function(obj:TrackableEntity):Object {
    obj.changedProps = [];
    obj.complexChangedProps = [];
    obj.changedCollectionProps = [];

    // obj.key prop is included in noTrackProps, but include it in cleanObj.
    const cleanObj:any = _.omitBy(_.omit(obj, _.without(obj.noTrackProps, obj.key??'')), _.isFunction)
    _.forOwn(cleanObj, function (value, key) {
      // Get clean Collection
      if(Array.isArray(value)) {
        cleanObj[key] = (value as TrackableEntity[]).map(x => TrackableEntity.getAcceptedEntity(x));
      }
      else if(value !== null && typeof value === "object") {
        cleanObj[key] = TrackableEntity.getAcceptedEntity(value);
      }
    });

    return cleanObj;
  }

  //<save-sqlinfo: Add parameter key>
  static acceptChanges = function (entity:TrackableEntity, key:string):void {
    //setCleanState(TrackableEntity.getAcceptedEntity(this));
    // debugger;
    setCleanStateStorage(key, TrackableEntity.getAcceptedEntity(entity));
  }


  /**
   * Restore changed properties to their original value
   */
  static rejectChanges = function (me:ChangeTrackable) {
    me.changedProps.forEach(cp => {
      (me as any)[cp.propertyName] = cp.oldValue;
    });

    me.complexChangedProps.forEach(xcp => {
      if(xcp.complexItem === null) return;
      //xcp.complexItem.changeTrackable?.rejectChanges();
      if(xcp.complexItem.changeTrackable)
        TrackableEntity.rejectChanges(xcp.complexItem.changeTrackable);
    });

    me.changedCollectionProps.forEach(ccp => {
      let collObj = (me as any)[ccp.collectionName] as any[];
      ccp.addedItems.forEach(obj => {
        // Remove      
        if(obj.keyPropertyName)
          _.pullAt(collObj, _.findIndex(collObj, x => x[obj.keyPropertyName || ''] === obj.keyValue));
      });
      ccp.deletedItems.forEach(obj => {
        // Add
        collObj.push(obj.changeTrackable);
      });
      ccp.changedItems.forEach(obj => {
        // rejectChanges
        //obj.changeTrackable?.rejectChanges();
        if(obj.changeTrackable) TrackableEntity.rejectChanges(obj.changeTrackable);
      });
    });
  };
  
  getDbMappings(a?:any):IDictionary<DBMappingInfo> {
    return {};
  }

  /**
   * There may be some props of thisEntit which is read as null.
   * When those properties are not changed before save, then at backend side,
   * those value may cause query error for non-nullable columns.
   * So, at initial stage, let's respect default value instead of null.
   * @param thisEntity 
   * @param initData 
   * @returns 
   */
  applyInitDataExcludingNull(thisEntity:any, initData:any) {
    if(!initData) return;
    for(const [key, value] of Object.entries(initData)) {
      if(value  !== null) {
          thisEntity[key] = value;
      }
    }
  }
}

/**
 * Update curEntity.changedProps: ChangedProperty[], 
 *        curEntity.complexChangedProps: ComplexChangedProperty[], 
 *        curEntity.changedCollectionProps: CollectionChangedProperty[]
 * after checking propName's value
 * @param propName 
 * @param orgEntity 
 * @param curEntity 
 * @returns 
 */
export function checkEntityPropChanges(propName: string, orgEntity: any, curEntity: any) {
    if(curEntity === null) return;

    const ctEntity = curEntity as ChangeTrackable;
    // skip no track props and arrow functions
    if (ctEntity.noTrackProps == null) return;      
    if (ctEntity.noTrackProps.indexOf(propName) >= 0 || _.isFunction(curEntity[propName])) return;

    let index = -1;
    if(Array.isArray(curEntity[propName])) {
      // Collection
      let keyName = '';
      if(orgEntity[propName].length > 0)
        keyName = orgEntity[propName][0].key;   //<pt220207: exception when curEntity = [], use orgEntity>
      
      if(!keyName && curEntity[propName].length > 0)
        keyName = curEntity[propName][0].key;
      
      if(orgEntity[propName].length === 0 && curEntity[propName].length  === 0) {
        // [issue#169] Both orgColl and curColl is empty, then remove changedCollectionProps for this collection if exist.
        const ccPropIndex = ctEntity.changedCollectionProps.findIndex(x => x.collectionName === propName);
        if(ccPropIndex >= 0) ctEntity.changedCollectionProps.splice(ccPropIndex, 1);
        return;
      }

      if(!keyName) return;

      const orgColl = _.sortBy(orgEntity[propName], function(row) { 
        if (row[keyName] < 0)
          return row[keyName] * -1; // new id인 경우 (-1, -2, -3...) 큰 값(-1이 제일 위) 순으로 정렬되어야함
        else
          return row[keyName]; // edit id인 경우 (1, 2, 3) 작은 값 (1이 제일 위) 순으로 정렬되어야함
      });
      const curColl = _.sortBy(curEntity[propName], function(row) { 
        if (row[keyName] < 0)
          return row[keyName] * -1; // new id인 경우 (-1, -2, -3...) 큰 값(-1이 제일 위) 순으로 정렬되어야함
        else
          return row[keyName]; // edit id인 경우 (1, 2, 3) 작은 값 (1이 제일 위) 순으로 정렬되어야함
      });
      //<pt220426: Use CompareCollection>
      //if(!_.isEqual(orgColl, curColl)) {
      if (!CompareTrackableCollection(orgColl, curColl)) {
        let ccProp = ctEntity.changedCollectionProps.find(x => x.collectionName === propName);
        if (!ccProp) {
          ccProp = { collectionName: propName, addedItems: [], changedItems: [], deletedItems: []};
          ctEntity.changedCollectionProps.push(ccProp);
        }

        // Collection item level
        const itemsToRemoveFromOrgCollection: number[] = [];
        _.forEach(orgColl, (obj) => {
          // Check if deleted
          if (!curColl.find(x => x[keyName] === obj[keyName])) {
            if (!obj[keyName] || obj[keyName] <= 0) {
              itemsToRemoveFromOrgCollection.push(orgColl.indexOf(obj));
            } else { 
              //<pt220207: Push only when already not in deletedItems>
              const delIndex = ccProp?.deletedItems.findIndex(d => d.keyPropertyName === keyName && d.keyValue === obj[keyName])??-1;
              if(delIndex < 0) {
                ccProp?.deletedItems.push({ keyPropertyName: keyName, keyValue: obj[keyName], changeTrackable: obj });
                //<pt220906: if obj to delete is included in changedItems, then remove it from collection
                const chgIndex = ccProp?.changedItems.findIndex(c => c.keyValue === obj[keyName])??-1;
                if(chgIndex >= 0) {
                  ccProp?.changedItems.splice(chgIndex, 1);
                }
                //pt220906>
              }
            }
          }
        });

        // Cleanup not saved entities to remove
        if (itemsToRemoveFromOrgCollection.length > 0) {
          // remove from addedItems
          _.forEach(itemsToRemoveFromOrgCollection, (i) => {
            let addIndex = ccProp?.addedItems.findIndex(x => x.keyValue === orgColl[i][keyName])
            if(addIndex && addIndex >= 0 && ccProp) _.pullAt(ccProp?.addedItems, [addIndex]);
          });
          _.pullAt(orgColl, itemsToRemoveFromOrgCollection);
        }

        // Added or changed
        if (curColl.length > 0) {
          _.forEach(curColl, (obj) => {
            // Find orgObj
            const orgObj = orgColl.find(x => x[keyName] === obj[keyName]);
            const ctObj = obj as ChangeTrackable;

            // Check new entity
            if ((!obj[keyName] || obj[keyName] <= 0) && !orgObj)  {
              // Push only when there is not addedItem with same keyValue.
              
              // <chuck date="2024-10-31" desc="New 모드에서 이미 들어 addedItems에 값이 있는 경우 새로 변경된 것이 Update 되지 않는 문제 수정.">
              //if(!ccProp?.addedItems.some(x => x.keyValue === obj[keyName])) {
              //  ccProp?.addedItems.push({ keyPropertyName: keyName, keyValue: obj[keyName], changeTrackable: obj });
              //}
              const index = ccProp?.addedItems.findIndex(x => x.keyValue === obj[keyName]) ?? -1;
              if (index >= 0) {
               ccProp?.addedItems.splice(index, 1);
              }
              ccProp?.addedItems.push({ keyPropertyName: keyName, keyValue: obj[keyName], changeTrackable: obj });
              // </chuck>
            }
            else {
              // Collection item's property level
              //<pt220426: Use CompareTrackableObject>
              //if(!_.isEqual(obj, orgObj)) {
              if (!CompareTrackableObjects(obj, orgObj)) {
                _.keys(obj).forEach(k => {
                  checkEntityPropChanges(k, orgObj, obj);
                });
              } else {
                // Clear dirty if it was.
                if(ctObj.changedProps.length > 0) ctObj.changedProps = [];
                if(ctObj.complexChangedProps.length > 0) ctObj.complexChangedProps = [];
                if(ctObj.changedCollectionProps.length > 0) ctObj.changedCollectionProps = [];
              }
              
              let changedIndex = ccProp?.changedItems.findIndex(x => x.keyValue === obj[keyName]) ?? -1;
              if(ctObj.isDirty) {
                if(changedIndex < 0) {
                  ccProp?.changedItems.push({ keyPropertyName: keyName, keyValue: obj[keyName], changeTrackable: obj });
                } else if(ccProp?.changedItems[changedIndex].changeTrackable !== obj) {
                  // Replace changeItem by current one
                  if(ccProp) ccProp.changedItems[changedIndex].changeTrackable = obj;
                }
              } else {
                // If obj is in changedItems, then remove it from there
                if(changedIndex >= 0 && ccProp) _.pullAt(ccProp?.changedItems, changedIndex);
              }
            }
          });

          //<pt220706: [github-issue#180-10] Cleanup addedItems when its count is greater than the number of curColl's new items.
          if(ccProp.addedItems.length > curColl.filter(e => e[keyName] < 0).length ) {
            _.remove(ccProp.addedItems, function(x) { 
              // Remove addedItem if it does not exist in curColl
              return curColl.findIndex(e => e[keyName] === x.keyValue) < 0; 
            });
          }
          //pt220706>
        } else if(ccProp) {
          // [issue#169] if curColl has no element, then clear addedItems, changedItems.
          if(ccProp.addedItems.length > 0) ccProp.addedItems.splice(0);
          if(ccProp.changedItems.length > 0) ccProp.changedItems.splice(0);
        }

        // Determine to keep ccProp
        if(ccProp.addedItems.length <= 0 && ccProp.changedItems.length <= 0 && ccProp.deletedItems.length <= 0) {
          const ccIndex = ctEntity.changedCollectionProps.indexOf(ccProp);
          ctEntity.changedCollectionProps.splice(ccIndex, 1);
        }
      } else {
        //<pt220426: Same Collection => Remove ColletionChangedProperty if remains.
        let ccProp = ctEntity.changedCollectionProps.find(x => x.collectionName === propName);
        if(ccProp) {
          const ccIndex = ctEntity.changedCollectionProps.indexOf(ccProp);
          ctEntity.changedCollectionProps.splice(ccIndex, 1);
        }
        //pt220426>
      }

    } else if(typeof curEntity[propName] === 'object') {
      // Complex object (typeof null === 'object')
      let cplxObj:ChangeTrackable = curEntity === null ? null : curEntity[propName];
      if(_.isEmpty(cplxObj)) {
        // if((orgEntity === null || _.isEmpty(orgEntity[propName]))) {
        if((orgEntity === null || orgEntity === undefined || _.isEmpty(orgEntity[propName]))) {
          // Checked cahngedProps and remove if exist
          index = ctEntity.changedProps.findIndex(x => x.propertyName === propName);
          if(index >= 0)  _.pullAt(ctEntity.changedProps, index);
          return;
        } else {
          //<pt220121: milestone timestamp change to null case.>
          let changeProp = ctEntity.changedProps.find(x => x.propertyName === propName);
          if(!changeProp) {
            ctEntity.changedProps.push({ propertyName: propName, 
              oldValue: orgEntity ? orgEntity[propName] : undefined, 
              newValue: curEntity ? curEntity[propName] : undefined});
          } else if(changeProp.newValue !== curEntity[propName]) {
            // Update newValue
            changeProp.newValue = curEntity[propName];
          }
        }
        return;
      }

      _.keys(cplxObj).forEach(k => {
        checkEntityPropChanges(k, orgEntity[propName], curEntity[propName]);
      });

      //cplxObj.isDirty = cplxObj.changedProps.length > 0 || cplxObj.complexChangedProps.length > 0 || cplxObj.changedCollectionProps.length > 0;
      if(cplxObj.isDirty) {
        index = ctEntity.complexChangedProps.findIndex(x => x.complexName === propName);
        if(index < 0) ctEntity.complexChangedProps.push({
          complexName: propName,
          complexItem: { keyPropertyName: cplxObj.key, keyValue: cplxObj.key ? curEntity[propName][cplxObj.key] : undefined, changeTrackable: cplxObj}
        });
      } else {
        // if not dirty and exist in complexChangedProps, then remove it.
        index = ctEntity.complexChangedProps.findIndex(x => x.complexName === propName);
        if(index >= 0) _.pullAt(ctEntity.complexChangedProps, index);
      }
    } else {
      // primitives
      if(orgEntity === null && curEntity === null) {
        ctEntity.changedProps = [];
        ctEntity.complexChangedProps = [];
        ctEntity.changedCollectionProps = [];
        return;
      }
      if(((_.isEmpty(orgEntity) && !_.isEmpty(curEntity)) || (!_.isEmpty(orgEntity) && _.isEmpty(curEntity))) || 
        !ComparePrimitives(orgEntity[propName], curEntity[propName])) {
        let changeProp = ctEntity.changedProps.find(x => x.propertyName === propName);
        if(!changeProp) {
          ctEntity.changedProps.push({ propertyName: propName, 
            oldValue: orgEntity ? orgEntity[propName] : undefined, 
            newValue: curEntity ? curEntity[propName] : undefined});
        } else if(changeProp.newValue !== curEntity[propName]) {
          // Update newValue
          changeProp.newValue = curEntity[propName];
        }
      }
      else {  // same as original
        // If p exists in changedProps, then remove it.
        index = ctEntity.changedProps.findIndex(x => x.propertyName === propName);
        if(index >= 0)  _.pullAt(ctEntity.changedProps, index);
      }
    }
}

/**
 * pt220220: Add check for ISO8601 date string
 * @param orgVal 
 * @param curVal 
 * @return true for same, false for different
 */
function ComparePrimitives(orgVal: any, curVal: any): boolean {
  if(!_.isEqual(orgVal, curVal)) {
    if(isIsoDateTime(orgVal) && isIsoDateTime(curVal)) {
      return compareIso(orgVal, curVal) === 0;
    } else {
      return false;
    }
  }
  return true;
}

function CompareTrackableCollection(orgColl:Array<any>, curColl:Array<any>): boolean {
  if(orgColl.length !== curColl.length) return false;
  // Check each array element
  let result = true;
  for(let i = 0; i < orgColl.length; i++) {
    if(!CompareTrackableObjects(orgColl[i], curColl[i])) {
      result = false
      break;
    }
  }
  return result;
}

function CompareTrackableObjects(obj1: any, obj2: any): boolean {
  let result = true;  // Equal
  const keys = _.keys(obj1);
  for(const key of keys) {
    if(['changedCollectionProps', 'changedProps', 'complexChangedProps', '_trackable', 'noTrackProps', 'getDateProps', 'getdateProps', 'key'].indexOf(key) < 0) {
      if(obj2 && obj1[key] !== obj2[key]) {
        result = false;
        break;
      }
    }
  }
  return result;
}


